<script lang="ts">
import { defineComponent, onMounted, Ref, ref, watch } from 'vue'
import { get, map } from 'lodash'
import { dialogService } from '@siteminder/sui-core/services'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import { extend } from 'vee-validate'
import * as authService from '@/services/auth'
import { ActivateInput, Tenant, User } from '@/types'
import { PASSWORD_EXPRESSION } from '@/constants'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { useI18n } from '../setup/setup-i18n'
import { SubmitPayload } from './types'
import { useRoute, router } from '../router'

const { useState: useUserStoreState } = createNamespacedHelpers('User')
const { useState: useLoginDomainStoreState } = createNamespacedHelpers('LoginDomain')

enum SecretQuestion {
  MotherMaidenName = 'mother-maiden-name',
  FatherMiddleName = 'father-middle-name',
  TownWhereBorn = 'town-where-born',
  FirstOverseasTrip = 'first-overseas-trip',
  FirstSchool = 'first-school',
  FirstPetName = 'first-pet-name',
}

interface Props {
  submit: (payload: SubmitPayload) => Promise<void>
  validateToken: (token: string) => Promise<void>
  requiresToken?: boolean
  email?: string | null
  hideSecretQuestion?: boolean
}

export default defineComponent<Props>({
  name: 'Activate',

  components: {
    DefaultLayout,
  },

  props: {
    submit: {
      type: Function,
      required: true,
    },
    validateToken: {
      type: Function,
      required: true,
    },
    // false for a page reached without ?token= (the order page): validateToken still runs, with no token
    requiresToken: {
      type: Boolean,
      default: true,
    },
    // shown read-only when set
    email: {
      type: String,
      default: null,
    },
    hideSecretQuestion: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {

    const { t } = useI18n()

    const user = useUserStoreState(['user']).user as Ref<User>

    const tenant = useLoginDomainStoreState(['tenant']).tenant as Ref<Tenant>

    onMounted(() => {

      const currentRoute = useRoute()

      return authService.checkSession(tenant.value)
        .then(() => {

          // User is logged in, they cannot activate their account without logging out
          dialogService({
            title: t('ActivatePage.error.mustLogoutTitle'),
            bodyContent: t('ActivatePage.error.mustLogoutMessage'),
            confirmButtonText: t('ActivatePage.error.mustLogoutConfirm'),
            showCancel: false,
            closeOnClickModal: false,
            closeOnPressEscape: false,
            beforeClose: (close) => {
              router.push({
                name: 'LogoutPage',
                query: {
                  redirectUri: window.location.href,
                },
              })
              return close()
            },
            beforeConfirm: (confirm) => {
              router.push({
                name: 'LogoutPage',
                query: {
                  redirectUri: window.location.href,
                },
              })
              return confirm()
            },
          })

        })
        .catch(() => {

          // User is not logged in, allow them to activate an account
          handleToken(currentRoute.value?.query.token as string)

        })

    })

    // these are template refs which can be referenced in the template
    const form = ref(null)
    const password = ref(null)
    const confirmPassword = ref(null)

    const loading = ref(false)
    const errorMessage = ref(null)

    const secretQuestions = map(SecretQuestion, (value) => {
      return {
        code: value,
        label: t(`ActivatePage.secretQuestions.${value}`) as string,
      }
    })

    const draft: Ref<ActivateInput> = ref({
      token: null,
      password: null,
      confirmPassword: null,
      secretQuestion: null,
      secretAnswer: null,
    })

    const passwordExpressionRegex = new RegExp(PASSWORD_EXPRESSION)

    watch(draft, async (newDraft) => {

      if (newDraft.password && password.value.validate) {
        password.value.validate()
      }

      if (newDraft.confirmPassword && confirmPassword.value.validate) {
        confirmPassword.value.validate()
      }

    }, { deep: true })

    // custom validation rules for the password and confirm password fields
    extend('password', (value: string) => {

      if (!passwordExpressionRegex.test(value)) {
        return t('Common.passwordFieldFormatError') as string
      }

      if (draft.value.password
      && draft.value.confirmPassword
      && draft.value.password !== draft.value.confirmPassword) {
        return t('Common.passwordFieldMatchError') as string
      }

      return true

    })

    const handleSubmit = async () => {

      errorMessage.value = null

      await form.value.validate().then(async (valid: boolean) => {

        if (!valid) {
          return
        }

        loading.value = true

        try {

          const payload = { ...draft.value }

          delete payload.confirmPassword

          if (props.hideSecretQuestion) {
            delete payload.secretQuestion
            delete payload.secretAnswer
          }

          await props.submit(payload)

        } catch (error) {

          if (get(error, 'response.data.code') === 'PasswordHistoryError') {
            errorMessage.value = t('ActivatePage.error.passwordHistory') as string
            return
          }

          if (['TokenInvalid', 'TokenExpired'].includes(error.message)) {
            router.push({ name: 'ActivateExpiredErrorPage' })
            return
          }

          router.replace({ name: 'UnknownErrorPage' })

        } finally {
          loading.value = false
        }

      })

    }

    const handleToken = async (token: string) => {

      if (!token && props.requiresToken) {
        router.replace({ name: 'UnknownErrorPage' })
        return
      }

      // Store the token on the activation draft
      draft.value.token = token

      try {

        await props.validateToken(token)

      } catch (error) {

        if (['TokenInvalid', 'TokenExpired'].includes(error.message)) {
          router.push({ name: 'ActivateExpiredErrorPage' })
          return
        }

        router.replace({ name: 'UnknownErrorPage' })

      }
    }

    return {
      t,
      loading,
      errorMessage,
      draft,
      secretQuestions,
      form,
      password,
      confirmPassword,
      handleSubmit,
      user,
    }

  },

})

</script>

<template>
  <div class="activate">
    <div class="activate-form">
      <default-layout
        :title="t('ActivatePage.pageTitle')"
        :sub-title="t('ActivatePage.subTitle')"
      >
        <sm-form
          ref="form"
          :disabled="loading"
          @submit="handleSubmit"
        >
          <template #default>
            <input
              type="email"
              name="email"
              class="sr-only"
              auto-complete="username"
              :value="email || user.email"
            >
            <fieldset
              data-heap-redact-attributes="value"
              data-heap-redact-text="true"
              :disabled="loading"
            >
              <sm-input
                v-if="email"
                data-heap-redact-text="true"
                data-testid="activate-email"
                :value="email"
                type="email"
                name="displayEmail"
                :label="t('LoginPage.emailLabel')"
                disabled
              />
              <sm-input
                data-heap-redact-text="true"
                ref="password"
                v-model="draft.password"
                type="password"
                name="password"
                auto-complete="new-password"
                autofocus
                :label="t('ActivatePage.passwordLabel')"
                :placeholder="t('ActivatePage.passwordPlaceholder')"
                rules="required|password"
              />
              <sm-input
                data-heap-redact-text="true"
                ref="confirmPassword"
                v-model="draft.confirmPassword"
                type="password"
                name="confirmPassword"
                auto-complete="new-password"
                :label="t('ActivatePage.confirmPasswordLabel')"
                :placeholder="t('ActivatePage.confirmPasswordPlaceholder')"
                rules="required|password"
              />
              <sm-select
                v-if="!hideSecretQuestion"
                v-model="draft.secretQuestion"
                :label="t('ActivatePage.secretQuestionLabel')"
                :options="secretQuestions"
                :placeholder="t('ActivatePage.secretQuestionPlaceholder')"
                rules="required"
              />
              <sm-input
                v-if="!hideSecretQuestion"
                v-model="draft.secretAnswer"
                name="secretAnswer"
                :label="t('ActivatePage.secretAnswerLabel')"
                :placeholder="t('ActivatePage.secretAnswerPlaceholder')"
                rules="required"
              />
              <sm-button
                type="primary"
                native-type="submit"
                :loading="loading"
              >
                {{ t('ActivatePage.submitButton') }}
              </sm-button>
            </fieldset>
            <fieldset>
              <sm-help-card
                v-if="errorMessage"
                type="warning"
              >
                <template #header>
                  {{ t('ActivatePage.error.passwordHistory') }}
                </template>
              </sm-help-card>
            </fieldset>
          </template>
        </sm-form>
      </default-layout>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// these are style overrides for the sui components to make them look similar to the previous sputnik components.
// TODO: remove these overrides once we implement new screens.
.sm-button {
  @apply w-full;
}

::v-deep .sm-button__content {
  @apply justify-center;
}

::v-deep .sm-select {
  @apply pb-6;
  padding-bottom: 16px !important;
  max-width: 291px !important;
}

  ::v-deep .sm-select-container{
    .sm-field-error {
      @apply hidden;
    }
  }

::v-deep .sm-field-label {
  padding-top: 0px !important;
}

.activate {
  @apply flex justify-center;

  .activate-form {
    @apply w-96;
  }

}

</style>
